import { useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useMutation } from '@tanstack/react-query'
import { GitMerge, AlertTriangle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

interface Rec { id: string; [k: string]: any }
interface Props {
  entityType: 'contact' | 'company'
  recordA: Rec
  recordB: Rec
  onClose: () => void
  onMerged: () => void
}

// High-signal referencing tables to COUNT for the blast-radius preview. The RPC
// reparents the full set regardless — these are the user-meaningful ones to show.
const PREVIEW: Record<'contact' | 'company', { table: string; col: string; label: string }[]> = {
  contact: [
    { table: 'deals', col: 'contact_id', label: 'deals' },
    { table: 'quotes', col: 'contact_id', label: 'quotes' },
    { table: 'invoices', col: 'contact_id', label: 'invoices' },
    { table: 'field_jobs', col: 'contact_id', label: 'jobs' },
    { table: 'conversations', col: 'contact_id', label: 'conversations' },
    { table: 'projects', col: 'contact_id', label: 'projects' },
  ],
  company: [
    { table: 'deals', col: 'company_id', label: 'deals' },
    { table: 'quotes', col: 'company_id', label: 'quotes' },
    { table: 'invoices', col: 'company_id', label: 'invoices' },
    { table: 'field_jobs', col: 'company_id', label: 'jobs' },
    { table: 'locations', col: 'company_id', label: 'locations' },
  ],
}

function nameOf(entityType: 'contact' | 'company', r: Rec): string {
  if (entityType === 'contact') return `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email || 'Contact'
  return r.name || r.domain || 'Company'
}
function subOf(entityType: 'contact' | 'company', r: Rec): string[] {
  return (entityType === 'contact' ? [r.email, r.company_name, r.phone] : [r.domain, r.email, r.industry]).filter(Boolean)
}

export default function MergeModal({ entityType, recordA, recordB, onClose, onMerged }: Props) {
  const [survivorId, setSurvivorId] = useState<string | null>(null)
  const [typed, setTyped] = useState('')
  const survivor = survivorId === recordA.id ? recordA : survivorId === recordB.id ? recordB : null
  const loser = survivorId ? (survivorId === recordA.id ? recordB : recordA) : null
  const noun = entityType === 'contact' ? 'contact' : 'company'

  // Loser's reference counts (only fetched once a survivor — hence a loser — is chosen).
  const { data: counts = [] } = useQuery({
    queryKey: ['merge-preview', entityType, loser?.id],
    queryFn: async () => {
      if (!loser) return []
      const rows = await Promise.all(PREVIEW[entityType].map(async s => {
        const { count } = await (supabase as any).from(s.table).select('id', { count: 'exact', head: true }).eq(s.col, loser.id)
        return { label: s.label, count: count || 0 }
      }))
      return rows.filter(r => r.count > 0)
    },
    enabled: !!loser,
  })

  const merge = useMutation({
    mutationFn: async () => {
      if (!survivorId || !loser) throw new Error('Pick a record to keep')
      const { error } = await (supabase as any).rpc(
        entityType === 'contact' ? 'merge_contacts' : 'merge_companies',
        { p_survivor: survivorId, p_loser: loser.id },
      )
      if (error) throw error
    },
    onSuccess: () => { toast.success(`${noun === 'contact' ? 'Contacts' : 'Companies'} merged`); onMerged() },
    onError: (e: any) => toast.error(e?.message || 'Merge failed'),
  })

  const canConfirm = !!survivorId && typed === 'MERGE' && !merge.isPending

  const card = (r: Rec) => {
    const isSurvivor = survivorId === r.id
    const isLoser = !!survivorId && !isSurvivor
    const border = isSurvivor ? 'var(--accent)' : isLoser ? 'var(--danger-border)' : 'var(--border-default)'
    return (
      <button type="button" onClick={() => setSurvivorId(r.id)}
        style={{ flex: 1, minWidth: 0, textAlign: 'left', padding: 14, borderRadius: 12, cursor: 'pointer',
          background: isLoser ? 'var(--danger-dim)' : 'var(--field-bg)', border: `1.5px solid ${border}` }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf(entityType, r)}</div>
        {subOf(entityType, r).map((s, i) => (
          <div key={i} style={{ fontSize: 12, color: 'var(--ink-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s}</div>
        ))}
        <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
          color: isSurvivor ? 'var(--accent-text)' : isLoser ? 'var(--danger)' : 'var(--ink-tertiary)' }}>
          {isSurvivor ? '✓ Keep this one' : isLoser ? 'Will be deleted' : 'Click to keep'}
        </div>
      </button>
    )
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }} onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 540, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24, maxHeight: '90vh', overflow: 'auto' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <GitMerge style={{ width: 18, height: 18, color: 'var(--accent-text)' }} />
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Merge two {noun === 'contact' ? 'contacts' : 'companies'}</h2>
        </div>
        <p style={{ fontSize: 13, color: 'var(--ink-secondary)', lineHeight: 1.6, margin: '0 0 16px' }}>
          Pick the record to <strong>keep</strong>. The other is permanently deleted and all of its references move to the one you keep. <strong>This cannot be undone.</strong>
        </p>

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>{card(recordA)}{card(recordB)}</div>

        {survivor && loser && (
          <div style={{ padding: 12, borderRadius: 10, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <AlertTriangle style={{ width: 13, height: 13, color: 'var(--warning)' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-secondary)' }}>What this does</span>
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--ink-secondary)', lineHeight: 1.6, margin: 0 }}>
              Moves {counts.length ? counts.map(c => `${c.count} ${c.label}`).join(', ') : 'all references'} from <strong>{nameOf(entityType, loser)}</strong> to <strong>{nameOf(entityType, survivor)}</strong>, then permanently deletes <strong>{nameOf(entityType, loser)}</strong>.
            </p>
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Type MERGE to confirm</label>
          <input value={typed} onChange={e => setTyped(e.target.value)} disabled={!survivorId} autoFocus
            style={{ width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace' }} />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 16px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, color: 'var(--ink-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => merge.mutate()} disabled={!canConfirm}
            style={{ padding: '9px 16px', background: canConfirm ? 'var(--accent)' : 'var(--hover-bg)', border: 'none', borderRadius: 9, color: canConfirm ? 'var(--accent-ink)' : 'var(--ink-tertiary)', fontSize: 13, fontWeight: 600, cursor: canConfirm ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 6 }}>
            <GitMerge style={{ width: 13, height: 13 }} /> {merge.isPending ? 'Merging…' : 'Merge'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}
